import { modelTypeTsm } from 'components/Pages/Jobs/inferenceFieldsOptions';
import { Inputs, ResultNameOrchestrations, ResultsInput } from 'interfaces/calculateFeatures';
import { StudyMultiplexInputs } from 'interfaces/job';
import { PipRunNameEnum } from 'interfaces/jobs/pipsRunNames';
import { compact, Dictionary, filter, forEach, isEmpty, toArray } from 'lodash';

export type InputCategory =
  | 'assignment_inputs'
  | 'tsm'
  | 'inference_results'
  | 'post_processed_results'
  | 'pips_cells_results'
  | 'pips_area_instances'
  | 'cell_quantification_results'
  | 'area_quantification_results'
  | 'slide_segmentations_table_results'
  | 'slide_detections_table_results';

export const inputCategoryLabels: Record<InputCategory, string> = {
  assignment_inputs: 'Annotation Assignment Inputs',
  tsm: 'Tissue Segmentation Mask',
  inference_results: 'Inference Results',
  post_processed_results: 'Post Processing Results',
  pips_cells_results: 'Post Processing - Cell Typing',
  pips_area_instances: 'Post Processing - Area Instances',
  cell_quantification_results: 'Cell Quantification Results',
  area_quantification_results: 'Area Quantification Results',
  slide_segmentations_table_results: 'Slide Segmentations Table Results',
  slide_detections_table_results: 'Spot Detections',
};

export const shouldHidePostProcessingInputsByDefault = (pipRunName?: string | null): boolean =>
  pipRunName !== PipRunNameEnum.CellTyping && pipRunName !== PipRunNameEnum.NeighborhoodClusters;

export type ArtifactCategoryByUrl = Record<string, InputCategory>;

export const buildArtifactCategoryByUrl = (params: {
  postprocessedArtifacts?: ResultNameOrchestrations[];
  pipsResultsArtifacts?: ResultNameOrchestrations[];
  pipsAreaInstancesArtifacts?: ResultNameOrchestrations[];
  cellQuantificationArtifacts?: ResultNameOrchestrations[];
  areaQuantificationArtifacts?: ResultNameOrchestrations[];
  slideSegmentationsTableArtifacts?: ResultNameOrchestrations[];
  slideDetectionsTableArtifacts?: ResultNameOrchestrations[];
}): ArtifactCategoryByUrl => {
  const {
    postprocessedArtifacts,
    pipsResultsArtifacts,
    pipsAreaInstancesArtifacts,
    cellQuantificationArtifacts,
    areaQuantificationArtifacts,
    slideSegmentationsTableArtifacts,
    slideDetectionsTableArtifacts,
  } = params;
  const categoriesByUrl: ArtifactCategoryByUrl = {};

  const addCategoryForArtifacts = (artifacts: ResultNameOrchestrations[] | undefined, category: InputCategory) => {
    forEach(artifacts, (resultNameOrchestrations) => {
      forEach(resultNameOrchestrations?.orchestrations, (orchestration) => {
        const artifactUrl = orchestration?.orchestrationResultArtifactUrlPattern;
        if (artifactUrl) {
          categoriesByUrl[artifactUrl] = category;
        }
      });
    });
  };

  addCategoryForArtifacts(postprocessedArtifacts, 'post_processed_results');
  addCategoryForArtifacts(pipsResultsArtifacts, 'pips_cells_results');
  addCategoryForArtifacts(pipsAreaInstancesArtifacts, 'pips_area_instances');
  addCategoryForArtifacts(cellQuantificationArtifacts, 'cell_quantification_results');
  addCategoryForArtifacts(areaQuantificationArtifacts, 'area_quantification_results');
  addCategoryForArtifacts(slideSegmentationsTableArtifacts, 'slide_segmentations_table_results');
  addCategoryForArtifacts(slideDetectionsTableArtifacts, 'slide_detections_table_results');

  return categoriesByUrl;
};

export type MissingInputsBySlideId = Record<string, InputCategory[]>;

export const getInputsBySlideIdFromJobInputs = (inputs: Inputs): Dictionary<ResultsInput[]> => {
  const inputsBySlideId: Dictionary<ResultsInput[]> = {};

  forEach(inputs, (inputsBySlideIdForCase) => {
    forEach(inputsBySlideIdForCase, (slideInputs, slideId) => {
      inputsBySlideId[slideId] = slideInputs;
    });
  });

  return inputsBySlideId;
};

export const filterInputsBySlideIds = (inputs: Inputs, selectedSlideIds: string[]): Inputs => {
  if (isEmpty(inputs) || isEmpty(selectedSlideIds)) {
    return inputs;
  }

  const selectedSlideIdSet = new Set(selectedSlideIds);
  const filteredInputs: Inputs = {};

  forEach(inputs, (inputsBySlideIdForCase, caseId) => {
    const filteredCaseInputs: Record<string, ResultsInput[]> = {};

    forEach(inputsBySlideIdForCase, (slideInputs, slideId) => {
      if (selectedSlideIdSet.has(slideId)) {
        filteredCaseInputs[slideId] = slideInputs;
      }
    });

    if (!isEmpty(filteredCaseInputs)) {
      filteredInputs[caseId] = filteredCaseInputs;
    }
  });

  return filteredInputs;
};

export type PipsStudyInputsState = {
  thresholds?: StudyMultiplexInputs['perMarkerThresholds'];
  perMarkerThresholdsOrchestrationId?: StudyMultiplexInputs['perMarkerThresholdsOrchestrationId'];
  cellRules?: StudyMultiplexInputs['cellRules'];
  knnArtifactUrl?: StudyMultiplexInputs['knnClassifier'] extends { artifactUrl: infer Url } ? Url : string;
  knnFileType?: StudyMultiplexInputs['knnClassifier'] extends { fileExtension: infer Ext } ? Ext : string;
  knnOrchestrationId?: string;
  neighborhoodClustersOrchestrationId?: string;
  kMeansClusteringResults?: StudyMultiplexInputs['kMeansClusteringResults'];
};

export const getArtifactOrchestrationId = (artifactUrl?: string | null) => {
  if (!artifactUrl) {
    return undefined;
  }

  const orchestrationIdWithSuffix = artifactUrl.split('orchestration_id=')[1];

  if (!orchestrationIdWithSuffix) {
    return undefined;
  }

  return orchestrationIdWithSuffix.split('&')[0] || undefined;
};

export const getPipsStudyInputsState = (pipsStudyInputs?: StudyMultiplexInputs | null): PipsStudyInputsState => {
  if (!pipsStudyInputs) {
    return {};
  }

  return {
    thresholds: pipsStudyInputs.perMarkerThresholds,
    perMarkerThresholdsOrchestrationId: pipsStudyInputs.perMarkerThresholdsOrchestrationId,
    cellRules: pipsStudyInputs.cellRules,
    knnArtifactUrl: pipsStudyInputs.knnClassifier?.artifactUrl,
    knnFileType: pipsStudyInputs.knnClassifier?.fileExtension,
    knnOrchestrationId: getArtifactOrchestrationId(pipsStudyInputs.knnClassifier?.artifactUrl),
    neighborhoodClustersOrchestrationId: pipsStudyInputs.neighborhoodClustersOrchestrationId,
    kMeansClusteringResults: pipsStudyInputs.kMeansClusteringResults,
  };
};

const getInputCategories = (
  resultInput: ResultsInput,
  artifactCategoryByUrl?: ArtifactCategoryByUrl
): InputCategory[] => {
  const categories = new Set<InputCategory>();
  const inputsToCheck = compact([resultInput?.input, resultInput?.override]);

  forEach(inputsToCheck, (input) => {
    if (input?.assignmentId) {
      categories.add('assignment_inputs');
    }

    if (input?.modelType) {
      if (input.modelType === modelTypeTsm.apiModelValue) {
        categories.add('tsm');
      } else {
        categories.add('inference_results');
      }
    }

    if (input?.artifactUrl && !input?.modelType) {
      const category = artifactCategoryByUrl?.[input.artifactUrl] ?? 'post_processed_results';
      categories.add(category);
    }
  });

  return toArray(categories) as InputCategory[];
};

const getRequiredInputCategories = (
  inputsBySlideId: Dictionary<ResultsInput[]>,
  artifactCategoryByUrl?: ArtifactCategoryByUrl
): InputCategory[] => {
  const requiredCategories = new Set<InputCategory>();

  forEach(inputsBySlideId, (slideInputs) => {
    forEach(slideInputs, (resultInput) => {
      forEach(getInputCategories(resultInput, artifactCategoryByUrl), (category) => requiredCategories.add(category));
    });
  });

  return toArray(requiredCategories) as InputCategory[];
};

export const getMissingInputsBySlideId = (params: {
  selectedSlideIds: string[];
  inputs: Inputs;
  artifactCategoryByUrl?: ArtifactCategoryByUrl;
}): MissingInputsBySlideId => {
  const { selectedSlideIds, inputs, artifactCategoryByUrl } = params;
  const inputsBySlideId = getInputsBySlideIdFromJobInputs(inputs);
  const requiredCategories = getRequiredInputCategories(inputsBySlideId, artifactCategoryByUrl);
  const missingInputsBySlideId: MissingInputsBySlideId = {};

  forEach(selectedSlideIds, (slideId) => {
    const slideInputs = inputsBySlideId[slideId] || [];
    const slideCategories = new Set<InputCategory>();

    forEach(slideInputs, (resultInput) => {
      forEach(getInputCategories(resultInput, artifactCategoryByUrl), (category) => slideCategories.add(category));
    });

    const missingCategories = filter(requiredCategories, (category) => !slideCategories.has(category));
    if (!isEmpty(missingCategories)) {
      missingInputsBySlideId[slideId] = missingCategories;
    }
  });

  return missingInputsBySlideId;
};
